import React, { FC, useState, useEffect } from "react";
import {
  FormControl,
  Autocomplete,
  TextField,
  TextFieldVariants,
} from "@mui/material";
import styles from "./SelectField.module.css";

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onChangeInputSelect?: (value: string) => void;
  options: { nome: string }[];
  variant?: TextFieldVariants;
  required?: boolean;
  fullWidth?: boolean;
  allowCustomOption?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disableClearable?: boolean;
  disableTyping?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const SelectField: FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  required,
  fullWidth = true,
  allowCustomOption,
  variant = "standard",
  containerClassName,
  inputClassName,
  error,
  helperText,
  disableClearable = false,
  disableTyping = false,
  disabled = false,
  placeholder = "Selecione",
}) => {
  const [inputValue, setInputValue] = useState(value);

  // Atualiza inputValue se value mudar no componente pai
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    newInputValue: string,
  ) => {
    if (disableTyping) {
      setInputValue(value);
      return;
    }

    setInputValue(newInputValue);
    onChange(newInputValue);
  };

  const handleValueChange = (
    event: React.ChangeEvent<{}>,
    newValue: string | { nome: string } | null,
  ) => {
    if (typeof newValue === "string") {
      onChange(newValue);
    } else if (newValue && "nome" in newValue) {
      onChange(newValue.nome);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} className={containerClassName}>
      <Autocomplete
        slotProps={{
          paper: {
            className: styles.paper,
          },
          popper: {
            className: styles.popper,
          },
        }}
        id={`${label.toLowerCase()}-autocomplete`}
        freeSolo={allowCustomOption}
        disableClearable={disableClearable}
        disabled={disabled}
        value={
          options.find((option) => option.nome === value) ||
          (allowCustomOption ? value : null)
        }
        onChange={(_, newValue) => handleValueChange(_, newValue)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.nome
        } // Garante que labels são strings
        isOptionEqualToValue={(option, value) =>
          typeof value === "string"
            ? option.nome === value
            : option.nome === value?.nome
        } // Evita erro de comparação
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            label={label}
            variant={variant}
            required={required}
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              readOnly: disableTyping || disabled,
              className: inputClassName || styles.inputSelect,
              disableUnderline: true,
            }}
            InputLabelProps={{
              className: styles.labelSelect, // estiliza o label
            }}
          />
        )}
        noOptionsText="não encontrado"
      />
    </FormControl>
  );
};

export default SelectField;

